import type { Messages } from '@/i18n/messages/index.ts'

/**
 * The two lookups that have to widen a wire string onto a guarded label map.
 *
 * `McpServer.lastProbeStatus` and `CurationQueueItem.risk` are both `z.string()` on the
 * contract, so neither map can be indexed by the value straight off the wire. These widen
 * the MAP rather than the value — the maps in the catalogue keep their `satisfies` guard, so
 * a new enum member is still a compile error there — and they name what happened when the
 * value is one this build has no words for.
 *
 * Before this existed, the first was written `stageLabel(status as never)`, and the cast was
 * the whole bug: any status the stage enum does not list produced `undefined` and an alert
 * badge with nothing in it — a red mark beside a system, saying nothing, on the one surface
 * whose job is to say what is wrong. The second rendered the raw identifier.
 *
 * They are catalogue-CONSUMING logic, so they live beside the components rather than inside
 * the messages tree (PRD-27 D-25, LOC-017), and they take the active pack as a parameter
 * rather than importing one — a module-scope catalogue read freezes the locale for the rest
 * of the session with nothing failing anywhere (F-3, LOC-012).
 */

export type SystemsPack = Messages['admin']['systems']

/** The probe status on a system row, which the contract types as a bare string. */
export const probeStatusLabel = (t: SystemsPack, status: string | null): string =>
  (status === null ? undefined : (t.probeStage as Record<string, string>)[status]) ?? t.stageUnhealthy

/** The risk class on a curation-queue row, which the contract likewise types as a bare string. */
export const riskClassLabel = (t: SystemsPack, risk: string): string =>
  (t.riskLabel as Record<string, string>)[risk] ?? t.riskUnclassified
